import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import { Theme } from "@ternary/web-ui-lib/theme/default";
import React, { useState } from "react";

const buttonGroupTheme = (baseTheme: Theme) => ({
  ...baseTheme,
  ButtonGroup_backgroundColor: baseTheme.background_color_disabled,
  ButtonGroup_backgroundColor_active: baseTheme.input_background_color,
});

const StyledButtonGroup = styled("span")<{
  theme?: Theme;
}>(({ theme: baseTheme }) => {
  const theme = buttonGroupTheme(baseTheme);

  return {
    padding: theme.space_xxs,
    borderRadius: theme.borderRadius_2,
    backgroundColor: theme.ButtonGroup_backgroundColor,
    gap: theme.space_xxs,
    display: "flex",
    button: {
      transition: "background-color 0.3s ease-in-out",
      display: "flex",
      flexDirection: "row",
      borderColor: theme.border_color,
      borderStyle: "solid",
      backgroundColor: theme.ButtonGroup_backgroundColor,
      borderRadius: theme.borderRadius_1,
    },
  };
});

const ContentWrapper = styled("span")<{
  theme?: Theme;
}>(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  gap: theme.space_xxs,
}));

const StyledText = styled("span")<{ theme?: Theme; isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    color: isSelected ? theme.text_color : theme.text_color_disabled,
    fontSize: `${theme.fontSize_small} !important`,
    fontWeight: "bold !important",
  })
);

const StyleNumberElements = styled("span")<{
  theme?: Theme;
}>(({ theme: baseTheme }) => {
  const theme = buttonGroupTheme(baseTheme);

  return {
    fontSize: `${theme.fontSize_xs} !important`,
    fontWeight: "500 !important",
    color: theme.text_color,
    borderRadius: theme.borderRadius_4,
    backgroundColor: theme.ButtonGroup_backgroundColor_active,
    border: `1px solid ${theme.border_color}`,
    padding: `${theme.space_xxs} ${theme.space_xxs}`,
  };
});

type Item<Type> = {
  label: string;
  type: Type;
  width?: number;
  length?: number;
};

type Props<Type = unknown> = {
  defaultChecked: number;
  items: readonly Item<Type>[];
  width?: number | string;
  onChange: (value: Type) => void;
};

export function ButtonGroup<Type>(props: Props<Type>): JSX.Element {
  const [selectedIndex, setSelectedIndex] = useState(props.defaultChecked ?? 0);
  const theme = useTheme();
  const buttonTheme = buttonGroupTheme(theme);

  return (
    <StyledButtonGroup>
      {props.items.map((item, i) => {
        const isSelected = selectedIndex === i;
        return (
          <Button
            key={`${i}-${item.label}`}
            width={props.width ?? item.width}
            onClick={() => {
              setSelectedIndex(i);
              props.onChange(item.type);
            }}
            style={
              isSelected
                ? {
                    background: buttonTheme.ButtonGroup_backgroundColor_active,
                    boxShadow: theme.box_shadow,
                  }
                : { borderWidth: 0 }
            }
            size={"small"}
          >
            <ContentWrapper>
              <StyledText isSelected={isSelected}>{item.label}</StyledText>
              {item.length !== undefined && (
                <StyleNumberElements>{item.length}</StyleNumberElements>
              )}
            </ContentWrapper>
          </Button>
        );
      })}
    </StyledButtonGroup>
  );
}

export default ButtonGroup;
